/**
 * El TEXTO y el QR de una factura recibida, leídos EN EL NAVEGADOR — `OD-0061`
 * (ACCEPTED 2026-10-24, opción A). Sin IA y sin servicios externos: la factura
 * no sale del dispositivo para leerla.
 *
 * - **PDF**: pdf.js saca el texto que el PDF ya lleva (exacto, sin OCR) de las
 *   primeras páginas y pinta la primera para buscar el QR (`OD-0054 D-08 C`,
 *   enmendada). Un PDF escaneado no lleva texto: esa página pintada va a
 *   Tesseract.
 * - **Foto**: jsQR busca el QR (`lector-qr.ts`) y Tesseract lee el texto.
 *
 * Las librerías (versiones FIJADAS en `package.json`, `D-03`) se cargan con
 * `import()` solo al pulsar el botón, y todo lo que necesitan —el worker de
 * pdf.js, el de Tesseract, su motor WebAssembly y el modelo de español— sale
 * del `dist` de la web, nunca de un CDN: Tesseract.js descarga de jsDelivr si
 * no se le dan las rutas, y la CSP (`connect-src 'self'`) lo impediría igual.
 *
 * Qué significa lo leído lo deciden las reglas de `lectura-factura.ts`.
 */
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import modeloEspanol from "@tesseract.js-data/spa/4.0.0_best_int/spa.traineddata.gz?url";
import motorBasico from "tesseract.js-core/tesseract-core-lstm.wasm.js?url";
import motorSimd from "tesseract.js-core/tesseract-core-simd-lstm.wasm.js?url";
import tesseractWorker from "tesseract.js/dist/worker.min.js?url";

import { leerQrDeImagen } from "./lector-qr";

/** Lado máximo del lienzo para leer: más no mejora el OCR y gasta memoria en el móvil. */
const LADO_MAXIMO = 2000;
/** Páginas de un PDF de las que se saca texto: el desglose está en la primera o la segunda. */
const PAGINAS_PDF = 3;
/** Por debajo de esto, el PDF no lleva texto (es un escaneado) y se lee como imagen. */
const MINIMO_TEXTO = 40;

export type LecturaFactura = {
  readonly texto: string;
  /** El texto del QR, si lo hay. Si es de la AEAT lo dice `datosDelQr`. */
  readonly qr: string | null;
  readonly origen: "PDF" | "OCR";
};

/** Un lienzo con la imagen, reducida a `LADO_MAXIMO`. */
async function lienzoDeImagen(fichero: Blob): Promise<HTMLCanvasElement | null> {
  let imagen: ImageBitmap;
  try {
    imagen = await createImageBitmap(fichero);
  } catch {
    return null;
  }
  const escala = Math.min(1, LADO_MAXIMO / Math.max(imagen.width, imagen.height));
  const lienzo = document.createElement("canvas");
  lienzo.width = Math.max(1, Math.round(imagen.width * escala));
  lienzo.height = Math.max(1, Math.round(imagen.height * escala));
  lienzo.getContext("2d")?.drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
  imagen.close();
  return lienzo;
}

const comoPng = (lienzo: HTMLCanvasElement): Promise<Blob | null> => new Promise((ok) => lienzo.toBlob(ok, "image/png"));

/** WebAssembly SIMD: el mismo módulo mínimo que usa `wasm-feature-detect`. Sin SIMD, el motor básico. */
function haySimd(): boolean {
  try {
    return WebAssembly.validate(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]));
  } catch {
    return false;
  }
}

/**
 * La carpeta del modelo: Tesseract pide `<carpeta>/spa.traineddata.gz`. Por eso
 * el fichero conserva su nombre en el `dist`, dentro de una carpeta con la
 * versión (`vite.config.ts`), y en desarrollo es el de `node_modules`.
 */
const CARPETA_MODELO = modeloEspanol.slice(0, modeloEspanol.lastIndexOf("/"));

/** El texto de una imagen, con Tesseract en un Worker. El modelo se lee de nuestro origen. */
async function ocr(lienzo: HTMLCanvasElement): Promise<string> {
  const { createWorker, OEM } = await import("tesseract.js");
  const worker = await createWorker("spa", OEM.LSTM_ONLY, {
    workerPath: tesseractWorker,
    langPath: CARPETA_MODELO,
    gzip: true,
    corePath: haySimd() ? motorSimd : motorBasico,
    // Un Worker desde blob: lo bloquearía `worker-src 'self'`.
    workerBlobURL: false,
    // La caché del navegador ya guarda el modelo; IndexedDB tendría otra copia.
    cacheMethod: "none",
  });
  try {
    const { data } = await worker.recognize(lienzo);
    return data.text;
  } finally {
    await worker.terminate();
  }
}

/** Las líneas de una página de PDF, juntando los trozos de texto que comparten altura. */
function lineasDePagina(items: readonly unknown[]): string[] {
  const trozos: { x: number; y: number; s: string }[] = [];
  for (const it of items) {
    if (typeof it !== "object" || it === null || !("str" in it) || !("transform" in it)) continue;
    const { str, transform } = it as { str: string; transform: number[] };
    if (str.trim() === "") continue;
    trozos.push({ x: transform[4] ?? 0, y: transform[5] ?? 0, s: str });
  }
  trozos.sort((a, b) => b.y - a.y || a.x - b.x);
  const lineas: { y: number; partes: { x: number; s: string }[] }[] = [];
  for (const t of trozos) {
    const linea = lineas.find((l) => Math.abs(l.y - t.y) < 3);
    if (linea !== undefined) linea.partes.push({ x: t.x, s: t.s });
    else lineas.push({ y: t.y, partes: [{ x: t.x, s: t.s }] });
  }
  return lineas.map((l) =>
    l.partes
      .sort((a, b) => a.x - b.x)
      .map((p) => p.s)
      .join(" "),
  );
}

async function leerPdf(fichero: Blob): Promise<LecturaFactura | null> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;
  // pdf.js 6 ya no evalúa código del PDF (sin `isEvalSupported`): la CSP sigue sin `'unsafe-eval'`.
  const tarea = pdfjs.getDocument({ data: new Uint8Array(await fichero.arrayBuffer()) });
  const doc = await tarea.promise;
  try {
    const lineas: string[] = [];
    for (let n = 1; n <= Math.min(PAGINAS_PDF, doc.numPages); n++) {
      const pagina = await doc.getPage(n);
      lineas.push(...lineasDePagina((await pagina.getTextContent()).items));
    }
    const primera = await doc.getPage(1);
    const base = primera.getViewport({ scale: 1 });
    const viewport = primera.getViewport({ scale: LADO_MAXIMO / Math.max(base.width, base.height) });
    const lienzo = document.createElement("canvas");
    lienzo.width = Math.round(viewport.width);
    lienzo.height = Math.round(viewport.height);
    await primera.render({ canvas: lienzo, viewport }).promise;
    const png = await comoPng(lienzo);
    const qr = png === null ? null : await leerQrDeImagen(png);
    const texto = lineas.join("\n");
    if (texto.replace(/\s/g, "").length >= MINIMO_TEXTO) return { texto, qr, origen: "PDF" };
    return { texto: await ocr(lienzo), qr, origen: "OCR" };
  } finally {
    await tarea.destroy();
  }
}

/**
 * Lee una foto o un PDF. `null` si no es ninguna de las dos cosas o no se ha
 * podido abrir: el formulario sigue igual y se escribe a mano.
 */
export async function leerFactura(fichero: File): Promise<LecturaFactura | null> {
  try {
    if (fichero.type === "application/pdf") return await leerPdf(fichero);
    if (!fichero.type.startsWith("image/")) return null;
    const qr = await leerQrDeImagen(fichero);
    const lienzo = await lienzoDeImagen(fichero);
    if (lienzo === null) return qr === null ? null : { texto: "", qr, origen: "OCR" };
    return { texto: await ocr(lienzo), qr, origen: "OCR" };
  } catch {
    return null;
  }
}
